import React, { useState, useEffect } from 'react';
import { AlertCircle, Play, Loader } from 'lucide-react';

const Testing = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedVariant, setSelectedVariant] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [apiKey, setApiKey] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = sessionStorage.getItem('token');
        
        // Fetch products
        const productsResponse = await fetch('/api/products?limit=100', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const productsData = await productsResponse.json();
        console.log('Products API Response:', productsData);
        if (productsData.success && productsData.data && Array.isArray(productsData.data.products)) {
          setProducts(productsData.data.products);
        }
        
        // Fetch user's API key
        const apiKeyResponse = await fetch('/api/api-keys', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const apiKeyData = await apiKeyResponse.json();
        console.log('API Key Response:', apiKeyData);
        if (apiKeyData.success && apiKeyData.data && apiKeyData.data.master_key) {
          setApiKey(apiKeyData.data.master_key.api_key);
          console.log('API Key loaded for testing:', apiKeyData.data.master_key.api_key);
        } else {
          console.error('No master API key found');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchData();
  }, []);

  const selectedProductData = products.find(p => String(p.id) === String(selectedProduct));
  const variants = selectedProductData && Array.isArray(selectedProductData.variants) ? selectedProductData.variants : [];

  const handleTest = async () => {
    if (!selectedProduct || !selectedVariant || !customerEmail) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setTestResult(null);

    try {
      const response = await fetch('/api/orders', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-API-Key': apiKey
        },
        body: JSON.stringify({
          product_id: parseInt(selectedProduct),
          variant_id: parseInt(selectedVariant),
          quantity: parseInt(quantity),
          customer_email: customerEmail,
          customer_data: { test_mode: true, timestamp: new Date().toISOString() }
        })
      });

      const data = await response.json();
      setTestResult(data);
      
      if (data.success) {
        alert('Test purchase completed successfully!');
      } else {
        alert('Test failed: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      setTestResult({ success: false, message: 'Network error: ' + error.message });
      alert('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loadingProducts) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px' }}>
        <Loader style={{ height: '32px', width: '32px', color: '#9ca3af' }} className="animate-spin" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header */}
      <div>
        <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'white', marginBottom: '4px' }}>
          API Link Testing
        </h2>
        <p style={{ fontSize: '14px', color: '#9ca3af' }}>
          Simulate API link purchases to test order processing and stock management
        </p>
      </div>

      {/* Debug Info */}
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #4a4a4a',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <p style={{ color: 'white', fontSize: '14px', marginBottom: '8px' }}>
          <strong>DEBUG INFO:</strong>
        </p>
        <p style={{ color: '#9ca3af', fontSize: '12px' }}>
          Products loaded: {products.length}
        </p>
        {products.length > 0 && (
          <p style={{ color: '#9ca3af', fontSize: '12px' }}>
            First product: {products[0]?.name || 'No name'} (ID: {products[0]?.id})
          </p>
        )}
        {products.length === 0 && (
          <p style={{ color: '#ef4444', fontSize: '12px' }}>
            No products found - API may have returned empty array
          </p>
        )}
      </div>

      {/* Main Form Card */}
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #4a4a4a',
        borderRadius: '8px',
        padding: '24px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Product Dropdown */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e5e5', marginBottom: '8px' }}>
              Product
            </label>
            <select
              value={selectedProduct}
              onChange={(e) => {
                setSelectedProduct(e.target.value);
                setSelectedVariant('');
              }}
              style={{
                width: '100%',
                height: '40px',
                borderRadius: '6px',
                border: '1px solid #4a4a4a',
                backgroundColor: '#0a0a0a',
                color: '#e5e5e5',
                padding: '0 12px',
                fontSize: '14px'
              }}
            >
              <option value="">Select a product</option>
              {products.map(product => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>

          {/* Variant Dropdown */}
          {selectedProduct && variants.length > 0 && (
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e5e5', marginBottom: '8px' }}>
                Variant
              </label>
              <select
                value={selectedVariant}
                onChange={(e) => setSelectedVariant(e.target.value)}
                style={{
                  width: '100%',
                  height: '40px',
                  borderRadius: '6px',
                  border: '1px solid #4a4a4a',
                  backgroundColor: '#0a0a0a',
                  color: '#e5e5e5',
                  padding: '0 12px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select a variant</option>
                {variants.map(variant => (
                  <option key={variant.id} value={variant.id}>
                    {variant.name} - ${variant.price}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Customer Email */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e5e5', marginBottom: '8px' }}>
              Customer Email
            </label>
            <input
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              placeholder="customer@example.com"
              style={{
                width: '100%',
                height: '40px',
                borderRadius: '6px',
                border: '1px solid #4a4a4a',
                backgroundColor: '#0a0a0a',
                color: '#e5e5e5',
                padding: '0 12px',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Quantity */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e5e5', marginBottom: '8px' }}>
              Quantity
            </label>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              style={{
                width: '100%',
                height: '40px',
                borderRadius: '6px',
                border: '1px solid #4a4a4a',
                backgroundColor: '#0a0a0a',
                color: '#e5e5e5',
                padding: '0 12px',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Submit Button */}
          <button
            onClick={handleTest}
            disabled={loading || !selectedProduct || !selectedVariant || !customerEmail}
            style={{
              width: '100%',
              height: '40px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: loading || !selectedProduct || !selectedVariant || !customerEmail ? '#4a4a4a' : '#3b198e',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: loading || !selectedProduct || !selectedVariant || !customerEmail ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {loading ? (
              <>
                <Loader style={{ height: '20px', width: '20px' }} className="animate-spin" />
                <span>Testing...</span>
              </>
            ) : (
              <>
                <Play style={{ height: '20px', width: '20px' }} />
                <span>Run Test Purchase</span>
              </>
            )}
          </button>

          {/* Test Result */}
          {testResult && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              borderRadius: '6px',
              border: testResult.success ? '1px solid #10b981' : '1px solid #ef4444',
              backgroundColor: testResult.success ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              color: testResult.success ? '#10b981' : '#ef4444',
              fontSize: '14px'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                {testResult.success ? '✓ Test Successful' : '✗ Test Failed'}
              </div>
              <div>{testResult.message || 'Unknown result'}</div>
              {testResult.data && testResult.data.order_number && (
                <div style={{ marginTop: '8px' }}>
                  <strong>Order Number:</strong> {testResult.data.order_number}
                </div>
              )}
              {testResult.data && testResult.data.error_code && (
                <div style={{ marginTop: '8px' }}>
                  <strong>Error Code:</strong> {testResult.data.error_code}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div style={{
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '8px',
        padding: '16px'
      }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <AlertCircle style={{ height: '20px', width: '20px', color: '#3b82f6', flexShrink: 0, marginTop: '2px' }} />
          <div style={{ fontSize: '14px', color: '#93c5fd' }}>
            <p style={{ fontWeight: '600', marginBottom: '8px' }}>Testing Information</p>
            <ul style={{ listStyleType: 'disc', paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <li>This simulates a customer purchase through an API link</li>
              <li>Orders are created in the database and can be viewed in the Orders page</li>
              <li>Manual stock variants will check availability and may return NO_STK error</li>
              <li>API stock variants will make actual API calls to external services</li>
              <li>Failed orders do not affect reseller balance or statistics</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Testing;
